import { stripHtmlSafe } from "@marketplace/shared/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  PACKAGE_AMOUNT_MAX_LIMIT,
  PACKAGE_PRICE_MAX_LIMIT
} from "../../constants";
import type { ProductCategoryType } from "../../types";

export const useBaseProductFormSchema = (
  productCategory: ProductCategoryType
) => {
  const { t } = useTranslation("product", {
    keyPrefix: "addProductPage.form.validations"
  });

  type TFunction = typeof t;

  const baseSchema = useMemo(
    () =>
      z.object({
        isRecurring: z.boolean().optional(),
        productId: z.string().default("-"),
        productCode: z.string().optional(),
        productName: z.string().min(1, {
          message: t("productNameRequired")
        }),
        productType: z
          .string()
          .min(1, {
            message: t("productTypeRequired")
          })
          .refine((val) => val !== "0", {
            message: t("productTypeRequired")
          }),
        version: z.string().default("-"),
        company: z.string().default("-"),
        productDescription: z
          .string()
          .min(1, {
            message: t("productDescriptionRequired")
          })
          .refine(
            (val) => {
              // Strip HTML tags and check if there's actual content
              const textContent = stripHtmlSafe(val);
              return textContent.length > 0;
            },
            {
              message: t("productDescriptionRequired")
            }
          ),
        link: z.string().optional(),
        industryCategory: z
          .array(z.string(), {
            message: t("industryCategoryRequired")
          })
          .min(1, {
            message: t("industryCategoryRequired")
          }),
        productService: z
          .string()
          .min(1, {
            message: t("productServiceRequired")
          })
          .refine((val) => val !== "0", {
            message: t("productServiceRequired")
          }),
        tncCheckout: z.string().min(1, {
          message: t("tncCheckoutRequired")
        }),
        tncTrial: z.string().optional(),
        tncRecurring: z.string().optional(),
        contactEmail: z.string().default("-"),
        packages: z
          .array(
            z.object({
              packageId: z.number().optional(),
              name: z.string().min(1, {
                message: t("packageNameRequired")
              }),
              isFreeTrial: z.boolean().optional(),
              specifications: z
                .array(
                  z.string().min(1, {
                    message: t("packageSpecificationRequired")
                  })
                )
                .min(1, {
                  message: t("packageSpecificationRequired")
                }),
              variants: z
                .array(
                  z.object({
                    variantId: z.number().optional(),
                    quantity: z
                      .number({
                        required_error: t("packageAmountRequired"),
                        invalid_type_error: t("packageAmountType")
                      })
                      .min(1, {
                        message: t("packageAmountRequired")
                      })
                      .max(PACKAGE_AMOUNT_MAX_LIMIT, {
                        message: t("packageAmountMax")
                      })
                      .multipleOf(1, {
                        message: t("packageAmountNoDecimal")
                      }),
                    unit: z.string().min(1, {
                      message: t("packageUnitRequired")
                    }),
                    price: z
                      .number({
                        required_error: t("packagePriceRequired"),
                        invalid_type_error: t("packagePriceType")
                      })
                      .min(0, {
                        message: t("packagePriceRequired")
                      })
                      .max(PACKAGE_PRICE_MAX_LIMIT, {
                        message: t("packagePriceMax")
                      })
                      .multipleOf(1, {
                        message: t("packagePriceNoDecimal")
                      }),
                    provisioningId: z.string().optional()
                  })
                )
                .min(1, {
                  message: "At least one variant is required"
                })
            })
          )
          .min(1, {
            message: t("packageRequired")
          }),
        pictures: z
          .array(
            z.object({
              mediaPath: z.string().nonempty(t("pictureRequired")),
              type: z.string().nonempty(t("pictureRequired")),
              file: z.instanceof(File).optional(),
              id: z.number().optional(),
              uid: z.string().optional()
            })
          )
          .min(1, { message: t("pictureRequired") }),
        provideTrial: z.boolean(),
        apiPackage: z
          .object({
            host: z.string().optional(),
            header: z.string().optional(),
            key: z.string().optional(),
            apis: z
              .array(
                z.object({
                  name: z.string().optional(),
                  endpoint: z.string().optional(),
                  method: z.string().optional(),
                  body: z
                    .array(
                      z.object({
                        key: z.string().optional(),
                        type: z.string().optional()
                      })
                    )
                    .optional(),
                  response: z.string().optional()
                })
              )
              .optional()
          })
          .optional(),
        showOnBuyer: z.boolean().optional()
      }),
    [t]
  );

  const schema = useMemo(() => {
    const withConditionTnc = (
      schema: typeof baseSchema | ReturnType<typeof baseSchema.merge>
    ) =>
      schema.superRefine((data, ctx) => {
        if (
          data.provideTrial &&
          (!data.tncTrial || data.tncTrial.trim() === "")
        ) {
          ctx.addIssue({
            path: ["tncTrial"],
            code: z.ZodIssueCode.custom,
            message: t("tncTrialRequired")
          });
        }

        if (
          data.isRecurring &&
          (!data.tncRecurring || data.tncRecurring.trim() === "")
        ) {
          ctx.addIssue({
            path: ["tncRecurring"],
            code: z.ZodIssueCode.custom,
            message: t("tncRecurringRequired")
          });
        }

        // Validate apiPackage is required for API category
        if (productCategory === "api") {
          validateApiPackage(data, ctx, t);
        }
      });

    const validateApiPackage = (
      data: {
        apiPackage?: {
          host?: string;
          header?: string;
          key?: string;
          apis?: Array<{
            name?: string;
            endpoint?: string;
            method?: string;
            response?: string;
            body?: Array<{ key?: string; type?: string }>;
          }>;
        };
      },
      ctx: z.RefinementCtx,
      t: TFunction
    ) => {
      if (!data.apiPackage?.host || data.apiPackage.host.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "host"],
          code: z.ZodIssueCode.custom,
          message: t("hostRequired")
        });
      }
      if (!data.apiPackage?.header || data.apiPackage.header.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "header"],
          code: z.ZodIssueCode.custom,
          message: t("headerRequired")
        });
      }
      if (!data.apiPackage?.key || data.apiPackage.key.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "key"],
          code: z.ZodIssueCode.custom,
          message: t("keyRequired")
        });
      }
      if (!data.apiPackage?.apis || data.apiPackage.apis.length === 0) {
        ctx.addIssue({
          path: ["apiPackage", "apis"],
          code: z.ZodIssueCode.custom,
          message: t("apisRequired")
        });
      }

      // Validate each API's required fields
      data.apiPackage?.apis?.forEach((api: any, apiIndex: number) => {
        validateApiFields(api, apiIndex, ctx, t);
      });
    };

    const validateApiFields = (
      api: {
        name?: string;
        endpoint?: string;
        method?: string;
        response?: string;
        body?: Array<{ key?: string; type?: string }>;
      },
      apiIndex: number,
      ctx: z.RefinementCtx,
      t: TFunction
    ) => {
      if (!api.name || api.name.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "apis", apiIndex, "name"],
          code: z.ZodIssueCode.custom,
          message: t("apiNameRequired")
        });
      }
      if (!api.endpoint || api.endpoint.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "apis", apiIndex, "endpoint"],
          code: z.ZodIssueCode.custom,
          message: t("apiEndpointRequired")
        });
      }
      if (!api.method || api.method.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "apis", apiIndex, "method"],
          code: z.ZodIssueCode.custom,
          message: t("apiMethodRequired")
        });
      }
      if (!api.response || api.response.trim() === "") {
        ctx.addIssue({
          path: ["apiPackage", "apis", apiIndex, "response"],
          code: z.ZodIssueCode.custom,
          message: t("apiResponseRequired")
        });
      }

      // Validate body type is required when method is not GET
      if (api.method && api.method !== "GET") {
        validateBodyFields(api, apiIndex, ctx, t);
      }
    };

    const validateBodyFields = (
      api: {
        body?: Array<{ key?: string; type?: string }>;
      },
      apiIndex: number,
      ctx: z.RefinementCtx,
      t: TFunction
    ) => {
      api.body?.forEach((bodyField: any, bodyIndex: number) => {
        if (
          bodyField.key &&
          (!bodyField.type || bodyField.type.trim() === "")
        ) {
          ctx.addIssue({
            path: ["apiPackage", "apis", apiIndex, "body", bodyIndex, "type"],
            code: z.ZodIssueCode.custom,
            message: t("bodyTypeRequired")
          });
        }
      });
    };

    return withConditionTnc(baseSchema);
  }, [productCategory, baseSchema, t]);

  return schema;
};
